import React, { useCallback, useEffect, useState } from 'react';
import { CourseInviteApi } from '../../services/api/CourseInviteApi';
import './MyCourseInvites.css';

/**
 * Student-facing banner listing pending course invitations the current user has
 * received by email. Accept/decline act directly from the list (no token needed
 * — the backend authorizes by the logged-in user's email). Renders nothing when
 * there are no pending invites.
 */
export default function MyCourseInvites({ onChanged }) {
  const [invites, setInvites] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await CourseInviteApi.getMyInvites();
      if (resp.ok) {
        const data = await resp.json();
        setInvites(Array.isArray(data) ? data : []);
      }
    } catch {
      /* ignore — banner simply stays hidden */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (invite, accept) => {
    setBusyId(invite.id);
    try {
      const resp = accept
        ? await CourseInviteApi.acceptInviteById(invite.id)
        : await CourseInviteApi.declineInviteById(invite.id);
      if (resp.ok) {
        setInvites((prev) => prev.filter((i) => i.id !== invite.id));
        if (accept && typeof onChanged === 'function') onChanged();
      }
    } catch {
      /* leave in place so the user can retry */
    } finally {
      setBusyId(null);
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="my-course-invites" role="list" aria-label="Course invitations">
      <div className="my-course-invites__title">Course invitations</div>
      {invites.map((inv) => (
        <div key={inv.id} className="my-course-invites__row" role="listitem">
          <div className="my-course-invites__info">
            <div className="my-course-invites__course">{inv.endeavorName || 'A course'}</div>
            {inv.inviterName && (
              <div className="my-course-invites__meta">Invited by {inv.inviterName}</div>
            )}
          </div>
          <div className="my-course-invites__actions">
            <button
              className="my-course-invites__btn my-course-invites__btn--accept"
              disabled={busyId === inv.id}
              onClick={() => respond(inv, true)}
            >
              Accept
            </button>
            <button
              className="my-course-invites__btn my-course-invites__btn--ghost"
              disabled={busyId === inv.id}
              onClick={() => respond(inv, false)}
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
